import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { Link, useNavigate } from "react-router-dom";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import LoadingOverlay from "../Dashboard/Common/LoadingOverlay";
import AuthShell from "./AuthShell";
import { decodeJWT, setToken, verifyLogin } from "../utilities/authHelper";

function Login({
  isAuthenticated = verifyLogin(),
  onLoginSuccess = () => {},
}) {
  const apiUrl = process.env.REACT_APP_API_URL;
  const navigate = useNavigate();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [passwordShown, setPasswordShown] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (isAuthenticated) {
      navigate("/home", { replace: true });
    }
  }, [isAuthenticated, navigate]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setErrorMessage("");
    setIsLoading(true);

    try {
      const response = await fetch(`${apiUrl}/auth:login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        setErrorMessage(
          data?.detail || "Login failed. Please check your credentials and try again.",
        );
        return;
      }

      if (data?.results?.access_token) {
        const payload = decodeJWT(data.results.access_token);
        const accountId = payload.account_id;

        setToken(
          data.results.access_token,
          data.results.user_id,
          data.results.role,
          accountId,
        );
        onLoginSuccess();
        navigate("/home", { replace: true });
      } else {
        setErrorMessage("Login failed. Please try again.");
      }
    } catch {
      setErrorMessage("Login failed. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Login | EMAlpha</title>
      </Helmet>

      {isLoading ? <LoadingOverlay /> : null}

      <AuthShell
        topBarPrompt="Need an account?"
        topBarCtaLabel="Create free account"
        topBarCtaTo="/signup"
      >
        <div>
          <p className="auth-card__eyebrow">Sign in</p>
          <h2 className="auth-card__title">Log in</h2>
        </div>

        <form className="auth-form" onSubmit={handleSubmit}>
          <div className="auth-field">
            <label htmlFor="login-email">Work email</label>
            <input
              id="login-email"
              type="email"
              name="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              placeholder="name@company.com"
              autoComplete="email"
              required
            />
          </div>

          <div className="auth-field">
            <label htmlFor="login-password">Password</label>
            <div className="auth-password-field">
              <input
                id="login-password"
                type={passwordShown ? "text" : "password"}
                name="password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                placeholder="Enter your password"
                autoComplete="current-password"
                required
              />
              <button
                type="button"
                className="auth-password-toggle"
                onClick={() => setPasswordShown((shown) => !shown)}
                aria-label={passwordShown ? "Hide password" : "Show password"}
              >
                {passwordShown ? <FaEyeSlash /> : <FaEye />}
              </button>
            </div>
          </div>

          {errorMessage ? (
            <div className="auth-alert" role="alert" aria-live="polite">
              {errorMessage}
            </div>
          ) : null}

          <div className="auth-meta auth-meta--end">
            <Link to="/forgot_password" className="auth-link">
              Reset password
            </Link>
          </div>

          <button type="submit" className="auth-submit">
            Log in
          </button>

          <p className="auth-footer-copy">
            Don&apos;t have an account?{" "}
            <Link to="/signup" className="auth-link">
              Create free account
            </Link>
          </p>
        </form>
      </AuthShell>
    </>
  );
}

export default Login;
